<script lang="ts">
  /**
   * Two-factor enrolment (Settings → Security).
   *
   * Three states: off → enrolling (QR + code) → on. The recovery codes are shown exactly
   * once, immediately after confirmation, because the server keeps only their HMACs.
   *
   * The scope note is deliberate and must stay visible: this protects the web session, not
   * IMAP/SMTP, so a user does not believe their mailbox is locked down further than it is.
   */
  import qrcode from 'qrcode-generator';
  import { ApiError } from '$lib/api/client';
  import { api } from '$lib/api/client';
  import { t } from '$lib/i18n';
  import type { TwoFactorStatus } from '$lib/api/types';

  let status = $state<TwoFactorStatus | null>(null);
  let secret = $state('');
  let uri = $state('');
  let code = $state('');
  let recovery = $state<string[]>([]);
  let busy = $state(false);
  let error = $state<string | null>(null);
  let note = $state<string | null>(null);
  let copied = $state(false);
  let disabling = $state(false);

  const enrolling = $derived(!!uri && !status?.enabled);
  const qrSvg = $derived(uri ? qrSvgFor(uri) : '');

  $effect(() => {
    void load();
  });

  async function load(): Promise<void> {
    try {
      status = await api.twofaStatus();
    } catch {
      status = null;
    }
  }

  /** An inline SVG, so nothing is fetched and the CSP stays `default-src 'self'`. */
  function qrSvgFor(text: string): string {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const n = qr.getModuleCount();
    let path = '';
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        if (qr.isDark(r, c)) path += `M${c} ${r}h1v1h-1z`;
      }
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-1 -1 ${n + 2} ${n + 2}" role="img" aria-label="QR">`
      + `<rect x="-1" y="-1" width="${n + 2}" height="${n + 2}" fill="#fff"/>`
      + `<path d="${path}" fill="#000"/></svg>`;
  }

  async function begin(): Promise<void> {
    busy = true;
    error = null;
    try {
      const r = await api.twofaSetup();
      secret = r.secret;
      uri = r.uri;
      code = '';
    } catch (e) {
      error = t(e instanceof ApiError && e.code === 'already_enabled' ? 'twoFactorOn' : 'adminFailed');
    } finally {
      busy = false;
    }
  }

  async function confirm(): Promise<void> {
    busy = true;
    error = null;
    try {
      const r = await api.twofaConfirm(code.trim());
      recovery = r.recovery;
      secret = '';
      uri = '';
      code = '';
      note = t('twoFactorEnabled');
      await load();
    } catch (e) {
      error = t(e instanceof ApiError && e.code === 'bad_code' ? 'twoFactorBadCode' : 'adminFailed');
    } finally {
      busy = false;
    }
  }

  async function disable(): Promise<void> {
    busy = true;
    error = null;
    try {
      await api.twofaDisable(code.trim());
      note = t('twoFactorDisabled');
      code = '';
      disabling = false;
      recovery = [];
      await load();
    } catch (e) {
      error = t(e instanceof ApiError && e.code === 'bad_code' ? 'twoFactorBadCode' : 'adminFailed');
    } finally {
      busy = false;
    }
  }

  function cancel(): void {
    secret = '';
    uri = '';
    code = '';
    error = null;
    disabling = false;
  }

  async function copyRecovery(): Promise<void> {
    try {
      await navigator.clipboard.writeText(recovery.join('\n'));
      copied = true;
      setTimeout(() => { copied = false; }, 2000);
    } catch {
      /* clipboard blocked: the codes are on screen to copy by hand */
    }
  }
</script>

<section class="security">
  <h3>{t('security')}</h3>
  <div class="card">
    <div class="row">
      <span class="label">{t('twoFactor')}</span>
      <span class="state" class:on={status?.enabled}>{status?.enabled ? t('twoFactorOn') : t('twoFactorOff')}</span>
    </div>
    <p class="muted">{t('twoFactorIntro')}</p>
    <p class="muted scope">{t('twoFactorImapNote')}</p>

    {#if note}<p class="ok" role="status">{note}</p>{/if}
    {#if error}<p class="err" role="alert">{error}</p>{/if}

    {#if recovery.length}
      <div class="recovery">
        <strong>{t('twoFactorRecovery')}</strong>
        <p class="muted">{t('twoFactorRecoveryIntro')}</p>
        <ul>
          {#each recovery as c (c)}<li>{c}</li>{/each}
        </ul>
        <div class="actions">
          <button type="button" onclick={copyRecovery}>{copied ? t('twoFactorCopied') : t('twoFactorCopy')}</button>
          <button type="button" class="primary" onclick={() => (recovery = [])}>{t('twoFactorDone')}</button>
        </div>
      </div>
    {:else if enrolling}
      <div class="enrol">
        <p>{t('twoFactorScan')}</p>
        <!-- eslint-disable-next-line svelte/no-at-html-tags -- locally generated SVG, no user input -->
        <div class="qr">{@html qrSvg}</div>
        <p class="muted">{t('twoFactorManual')}</p>
        <code class="secret" dir="ltr">{secret}</code>
        <label>
          <span>{t('twoFactorCode')}</span>
          <input type="text" dir="ltr" inputmode="numeric" autocomplete="one-time-code" maxlength="8" bind:value={code} />
        </label>
        <div class="actions">
          <button type="button" onclick={cancel}>{t('adminCancel')}</button>
          <button type="button" class="primary" disabled={busy || code.trim().length < 6} onclick={confirm}>{t('twoFactorConfirm')}</button>
        </div>
      </div>
    {:else if disabling}
      <div class="enrol">
        <label>
          <span>{t('twoFactorCode')}</span>
          <input type="text" dir="ltr" inputmode="numeric" autocomplete="one-time-code" maxlength="20" bind:value={code} />
        </label>
        <div class="actions">
          <button type="button" onclick={cancel}>{t('adminCancel')}</button>
          <button type="button" class="danger" disabled={busy || code.trim().length < 6} onclick={disable}>{t('twoFactorDisable')}</button>
        </div>
      </div>
    {:else if status?.enabled}
      <p class="muted">{t('twoFactorRecoveryLeft', { n: status.recoveryLeft })}</p>
      <div class="actions">
        <button type="button" onclick={() => { disabling = true; note = null; }}>{t('twoFactorDisable')}</button>
      </div>
    {:else}
      <div class="actions">
        <button type="button" class="primary" disabled={busy} onclick={begin}>{t('twoFactorEnable')}</button>
      </div>
    {/if}
  </div>
</section>

<style>
  /* @css settings/TwoFactor start */
  .card {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .label {
    font-weight: 600;
  }
  .state {
    font-size: 12px;
    padding: 2px 10px;
    border-radius: 999px;
    background: var(--surface-subtle);
    color: var(--text-muted);
  }
  .state.on {
    background: var(--ok-bg, #e7f7ed);
    color: var(--ok-fg, #0a7a35);
  }
  .muted {
    margin: 0;
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.7;
  }
  .scope {
    border-inline-start: 3px solid var(--border);
    padding-inline-start: 10px;
  }
  .qr {
    width: 200px;
    max-width: 100%;
    align-self: center;
    background: #fff;
    padding: 8px;
    border-radius: 8px;
  }
  .qr :global(svg) {
    display: block;
    width: 100%;
    height: auto;
  }
  .secret {
    align-self: center;
    letter-spacing: 2px;
    font-size: 14px;
    background: var(--surface-subtle);
    padding: 6px 10px;
    border-radius: 6px;
    user-select: all;
  }
  .enrol,
  .recovery {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .recovery ul {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .recovery li {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    background: var(--surface-subtle);
    border-radius: 6px;
    padding: 6px 8px;
    text-align: center;
    direction: ltr;
  }
  .actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .err {
    margin: 0;
    color: var(--danger, #c0392b);
    font-size: 13px;
  }
  .ok {
    margin: 0;
    color: var(--ok-fg, #0a7a35);
    font-size: 13px;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
  }
  input {
    padding: 8px 10px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface);
    color: var(--text);
    font: inherit;
  }
  /* @css settings/TwoFactor end */
</style>
